// TableComponents.jsx
import React from "react";
import { Card } from "./card";
import ThumbnailImage from "./ThumbnailImage";
import { HeartIcon } from '@heroicons/react/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/solid';


// Status badge component with configurable styles
const StatusBadge = ({ status, type = "default", className = "" }) => {
    // Define different status type configurations
    const statusConfigs = {
        // Application status
        application: {
            applied: { bg: "bg-warning-100", text: "text-warning-700", label: "Applied" },
            approved: { bg: "bg-success-100", text: "text-success-700", label: "Approved" },
            registered: { bg: "bg-success-100", text: "text-success-700", label: "Registered" },
            inprogress: { bg: "bg-warning-100", text: "text-warning-700", label: "In Progress" },
            declined: { bg: "bg-error-100", text: "text-error-700", label: "Declined" },
            pending: { bg: "bg-inProgress-100", text: "text-inProgress-600", label: "Payment Required" }
        },
        // Payment status
        payment: {
            paid: { bg: "bg-success-100", text: "text-success-700", label: "Paid" },
            "not-paid": { bg: "bg-error-100", text: "text-warning-700", label: "Not Paid" }
        },
        // Event status (if you need specific styling for events)
        event: {
            upcoming: { bg: "bg-inProgress-100", text: "text-inProgress-600", label: "Upcoming" },
            ongoing: { bg: "bg-success-100", text: "text-success-700", label: "Ongoing" },
            completed: { bg: "bg-grey-100", text: "text-grey-700", label: "Completed" },
            cancelled: { bg: "bg-error-100", text: "text-error-700", label: "Cancelled" },
            draft: { bg: "bg-warning-100", text: "text-warning-700", label: "Draft" },
            active: { bg: "bg-primary-100", text: "text-primary-700", label: "Active" },
            archived: { bg: "bg-grey-100", text: "text-grey-700", label: "Archived" }
        },
        // Default status (fallback)
        default: {
            registered: { bg: "bg-success-100", text: "text-success-700", label: "Registered" },
            applied: { bg: "bg-warning-100", text: "text-warning-700", label: "Applied" },
            "payment-required": { bg: "bg-inProgress-100", text: "text-inProgress-600", label: "Payment Required" }
        }
    };

    const statusConfig = statusConfigs[type]?.[status?.toLowerCase()] || {
        bg: "bg-grey-100",
        text: "text-grey-700",
        label: status || ""
    };

    return (
        <div className={`${className} w-full flex items-center justify-center`}>
            <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig.bg} ${statusConfig.text}`}
            >
                {/* Bullet point */}
                <span className="mr-1 text-base text-2xl leading-none">•</span>
                {statusConfig.label}
            </span>
        </div>
    );
};

// Column configuration for the table
const columnTypes = {
    text: (value) => <span className="text-sm text-grey-900">{value}</span>,
    date: (value) => <span className="text-sm text-grey-500">{value}</span>,
    image: (value, alt = "Image") => (
        <ThumbnailImage 
            src={value} 
            alt={alt || "Image"} 
            size="lg" 
            variant="logo"
            rounded="lg"
        />
    ),
    status: (value, type) => <StatusBadge status={value} type={type} />,
    email: (value) => (
        <a href={`mailto:${value}`} className="text-sm text-grey-900 hover:text-primary-500">
            {value}
        </a>
    ),
    phone: (value) => (
        <a href={`tel:${value}`} className="text-sm text-grey-900 hover:text-primary-500">
            {value}
        </a>
    ),
    button: (value, onClick) => {
        if (!value) return null;
        return (
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                }}
                className="px-3 py-2 bg-grey-900 text-white text-sm rounded-lg hover:bg-grey-800"
            >
                {value}
            </button>
        );
    },
    compound: (primary, secondary, secondaryStyles, underlinePrimary) => {
        return (
            <div className="flex flex-col">
                <span
                    className={`text-sm font-medium text-grey-900 ${underlinePrimary ? "underline underline-offset-2" : ""}`}
                    style={{ wordBreak: 'break-word' }}
                >
                    {primary}
                </span>
                <span 
                    className={`${secondaryStyles || ''} text-sm text-grey-500`}
                    style={{ wordBreak: 'break-word' }}
                >
                    {secondary}
                </span>
            </div>
        );
    },
    compoundContact: (primary, secondary, secondaryStyles) => {
        return (
            <div className="flex flex-col">
                <a
                    href={`tel:${primary}`}
                    className="text-sm font-medium text-grey-900 hover:text-primary-500"
                    style={{ wordBreak: 'break-word' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {primary}
                </a>
                <a 
                    href={`mailto:${secondary}`}
                    className={`${secondaryStyles || ''} text-sm text-grey-500 hover:text-primary-500`}
                    style={{ wordBreak: 'break-word' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {secondary}
                </a>
            </div>
        );
    }
};

const renderCellContent = (column, row, { onFavoriteToggle, favorites = [] }) => {
    if (column.type === "favorite") {
        const vendorId = row.mpsId || row.id;
        const isFavorite = favorites.some(f => f.mpsId === vendorId);
        return (
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    if (onFavoriteToggle) {
                        onFavoriteToggle({
                            mpsId: vendorId,
                            businessName: row.businessName || row.name || row.company,
                            businessType: row.businessType || row.category,
                            email: row.email || row.businessEmail,
                            image: row.image || row.companyLogo
                        });
                    }
                }}
                className={`p-1.5 rounded-full transition-all duration-200 ${
                    isFavorite
                        ? 'text-primary-500 hover:text-primary-600 hover:bg-primary-50'
                        : 'text-gray-300 hover:text-primary-400 hover:bg-gray-50'
                }`}
                title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            >
                {isFavorite ? (
                    <HeartSolidIcon className="w-5 h-5" />
                ) : (
                    <HeartIcon className="w-5 h-5" />
                )}
            </button>
        );
    }
    if (column.type === "button") {
        return columnTypes.button(row[column.key], () => column.onClick(row.id));
    }
    if (column.type === "compound") {
        return columnTypes.compound(row[column.primaryKey], row[column.secondaryKey], column.secondaryStyles, column.underlinePrimary);
    }
    if (column.type === "status") {
        return columnTypes.status(row[column.key], column.statusType);
    }
    if (column.render) {
        return column.render(row);
    }
    if (column.type) {
        return columnTypes[column.type](row[column.key]);
    }
    return columnTypes.text(row[column.key]);
};

const PebbleTable = ({ columns, data, noDataTitle, noDataSubTitle, noDataButtonText, noDataAction, onColumnClick, onFavoriteToggle, favorites = [] }) => {
    const cellProps = { onFavoriteToggle, favorites };

    // Empty state shared between desktop & mobile
    const emptyState = (
        <div className="flex flex-col items-center justify-center min-h-[300px] h-full">
            <h2 className="text-lg font-medium text-grey-700 mb-2">{noDataTitle}</h2>
            <p className="text-sm text-grey-500 mb-8">{noDataSubTitle}</p>
            {noDataButtonText && <button
                className="max-w-[158px] justify-center bg-grey-700 text-base-white rounded-md px-3 py-2 text-xs font-semibold hover:bg-primary-700 transition duration-150 ease-in-out"
                onClick={noDataAction}
            >
                {noDataButtonText}
            </button>}
        </div>
    );

    return (
        <Card className="mt-4 flex flex-col flex-grow overflow-hidden">
            {/* Desktop table view */}
            <div className="relative flex-grow flex-col hidden md:flex">
                <table className="w-full">
                    <thead className="bg-grey-50 border-b border-grey-200">
                        <tr>
                            {columns.map((column, index) => (
                                <th
                                    key={index}
                                    className="px-3 py-3 md:px-6 md:py-4 text-left text-sm font-semibold text-grey-700"
                                >
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    {data?.length > 0 ? (
                        <tbody className="divide-y divide-grey-200">
                            {data.map((row, rowIndex) => (
                                <tr
                                    key={rowIndex}
                                    className="hover:bg-grey-50"
                                    {...(onColumnClick ? { onClick: () => onColumnClick(row.id) } : {})}
                                >
                                    {columns.map((column, colIndex) => (
                                        <td key={colIndex} className="px-3 py-3 md:px-6 md:py-4">
                                            {renderCellContent(column, row, cellProps)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    ) : (
                        <tbody>
                            <tr>
                                <td colSpan={columns.length} className="h-full">
                                    {emptyState}
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>

            {/* Mobile card view */}
            <div className="md:hidden">
                {data?.length > 0 ? (
                    <div className="divide-y divide-grey-200">
                        {data.map((row, rowIndex) => (
                            <div
                                key={rowIndex}
                                className={`p-4 space-y-2 ${onColumnClick ? 'cursor-pointer hover:bg-grey-50 active:bg-grey-100' : ''}`}
                                onClick={onColumnClick ? () => onColumnClick(row.id) : undefined}
                            >
                                {columns.map((column, colIndex) => {
                                    // Skip empty headers (like favorite/image/action columns) - render inline
                                    if (!column.header && column.type === "favorite") {
                                        return (
                                            <div key={colIndex} className="flex justify-end">
                                                {renderCellContent(column, row, cellProps)}
                                            </div>
                                        );
                                    }
                                    if (!column.header && column.type === "image") {
                                        return (
                                            <div key={colIndex} className="flex justify-center">
                                                {renderCellContent(column, row, cellProps)}
                                            </div>
                                        );
                                    }
                                    if (!column.header && column.type === "button") {
                                        return (
                                            <div key={colIndex} className="flex justify-end pt-2">
                                                {renderCellContent(column, row, cellProps)}
                                            </div>
                                        );
                                    }
                                    if (!column.header && column.render) {
                                        return (
                                            <div key={colIndex} className="flex justify-end pt-2">
                                                {column.render(row)}
                                            </div>
                                        );
                                    }
                                    if (!column.header) return null;
                                    return (
                                        <div key={colIndex} className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-medium text-grey-500 flex-shrink-0">{column.header}</span>
                                            <div className="text-right">
                                                {renderCellContent(column, row, cellProps)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                ) : (
                    emptyState
                )}
            </div>
        </Card>
    );
};


// Tab component
const Tab = ({ label, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex-shrink-0 ${isActive
            ? "text-grey-900 underline underline-offset-8"
            : "text-grey-500 hover:text-grey-700"
            }`}
    >
        {label}
    </button>
);

export { Tab, PebbleTable, StatusBadge };